import type { Editor, SituacaoUso, Template } from './Templates.scopes'

// As mesmas regras do servidor (apps/server/src/templates/templates.regras.ts), para avisar enquanto o admin escreve.
// O servidor confere de novo antes de mandar à Meta.

export const LIMITE_CORPO = 1024
export const LIMITE_BOTOES = 10
export const LIMITE_TEXTO_BOTAO = 25

export function variaveisDoTexto(texto: string): string[] {
    return [...new Set([...texto.matchAll(/\{\{(\w+)\}\}/g)].map((m) => m[1]))]
}

export function problemasDoEditor(editor: Editor, existentes: readonly string[] = []): string[] {
    const problemas: string[] = []
    if (editor.modo === 'criar') {
        if (!/^[a-z0-9_]{1,512}$/.test(editor.nome)) problemas.push('Nome só com letras minúsculas, dígitos e "_".')
        if (existentes.includes(editor.nome)) problemas.push('Já existe um template com esse nome.')
        if (!/^[a-z]{2}(_[A-Z]{2})?$/.test(editor.idioma)) problemas.push('Idioma no formato pt_BR.')
    }
    const corpo = editor.corpo
    if (!corpo.trim()) problemas.push('Escreva o texto do template.')
    if (corpo.length > LIMITE_CORPO) problemas.push(`O texto passa de ${LIMITE_CORPO} caracteres.`)
    if (/\{\{(?!\s*[a-z_][a-z0-9_]*\s*\}\})[^}]*\}\}/.test(corpo)) {
        problemas.push('Variáveis só com letras minúsculas, dígitos e "_", começando por letra: {{nome}}.')
    }
    if (/^\s*\{\{/.test(corpo) || /\}\}\s*$/.test(corpo)) {
        problemas.push('O texto não pode começar nem terminar com uma variável.')
    }
    if (/\}\}\s*\{\{/.test(corpo)) problemas.push('Duas variáveis não podem ficar coladas.')
    for (const v of variaveisDoTexto(corpo)) {
        if (!`${editor.exemplos[v] ?? ''}`.trim()) problemas.push(`Dê um exemplo para {{${v}}}.`)
    }
    const botoes = editor.botoes.map((b) => b.trim())
    if (botoes.length > LIMITE_BOTOES) problemas.push(`No máximo ${LIMITE_BOTOES} botões.`)
    if (botoes.some((b) => !b)) problemas.push('Botão sem texto.')
    if (botoes.some((b) => b.length > LIMITE_TEXTO_BOTAO)) {
        problemas.push(`Texto de botão com mais de ${LIMITE_TEXTO_BOTAO} caracteres.`)
    }
    if (new Set(botoes).size < botoes.length) problemas.push('Dois botões com o mesmo texto.')
    return problemas
}

/** O texto com os exemplos no lugar das variáveis, como o parceiro vai ler. */
export function previa(corpo: string, exemplos: Record<string, string>): string {
    return corpo.replace(/\{\{(\w+)\}\}/g, (inteiro, nome: string) => exemplos[nome]?.trim() || inteiro)
}

/** Os templates de exemplo que a Meta cria em toda conta. */
export const ehExemploDaMeta = (nome: string) => nome.startsWith('sample_') || nome === 'hello_world'

/** Lista da tela: busca por nome e, se pedido, sem os exemplos da Meta. */
export function filtrarTemplates(templates: Template[], busca: string, mostrarExemplos: boolean): Template[] {
    const termo = busca.trim().toLowerCase()
    return templates.filter(
        (t) => (mostrarExemplos || !ehExemploDaMeta(t.nome)) && (!termo || t.nome.toLowerCase().includes(termo))
    )
}

export const ROTULO_SITUACAO_USO: Record<SituacaoUso, { rotulo: string; cor: 'success' | 'warning' | 'error' }> = {
    ok: { rotulo: 'Em uso', cor: 'success' },
    padrao: { rotulo: 'Padrão do código', cor: 'success' },
    sem_template: { rotulo: 'Sem template', cor: 'warning' },
    nao_encontrado: { rotulo: 'Não está na Meta', cor: 'error' },
    nao_aprovado: { rotulo: 'Não aprovado', cor: 'error' },
    incompativel: { rotulo: 'Incompatível', cor: 'error' }
}

export const ROTULO_STATUS: Record<string, { rotulo: string; cor: 'success' | 'warning' | 'error' | 'default' }> = {
    APPROVED: { rotulo: 'Aprovado', cor: 'success' },
    PENDING: { rotulo: 'Em análise', cor: 'warning' },
    IN_APPEAL: { rotulo: 'Em recurso', cor: 'warning' },
    REJECTED: { rotulo: 'Recusado', cor: 'error' },
    PAUSED: { rotulo: 'Pausado', cor: 'error' },
    DISABLED: { rotulo: 'Desativado', cor: 'error' }
}

export const ROTULO_CATEGORIA: Record<string, string> = {
    UTILITY: 'Utilidade',
    MARKETING: 'Marketing',
    AUTHENTICATION: 'Autenticação'
}

/** Só sai da conta o template que nenhum uso da LIA nem campanha aponta (o servidor confere de novo). */
export const podeExcluir = (t: Pick<Template, 'usos' | 'campanhas'>) => t.usos.length === 0 && !t.campanhas?.length

/** O template de uma campanha repete o conteúdo dela: muda-se pela campanha, não por aqui. */
export const podeEditar = (t: Pick<Template, 'campanhas'>) => !t.campanhas?.length

/** Editor de um template novo, em branco ou a partir do texto de um uso. */
export function editorNovo(uso?: { uso: string; nomeSugerido?: string; texto?: string; botoes: string[] }): Editor {
    return {
        modo: 'criar',
        nome: uso?.nomeSugerido ?? 'lia_',
        categoria: 'UTILITY',
        idioma: 'pt_BR',
        corpo: uso?.texto ?? '',
        exemplos: {},
        botoes: uso ? [...uso.botoes] : [],
        paraUso: uso?.uso
    }
}

export function editorDoTemplate(t: Template): Editor {
    return {
        modo: 'editar',
        templateId: t.id,
        nome: t.nome,
        categoria: t.categoria === 'MARKETING' ? 'MARKETING' : 'UTILITY',
        idioma: t.idioma,
        corpo: t.corpo,
        exemplos: {},
        botoes: [...t.botoes]
    }
}

/** Por que o template não atende o uso (a mesma regra do servidor); vazio quando atende. */
export function problemasParaUso(uso: { variaveis: string[]; botoes: string[] }, t: Template): string[] {
    const problemas: string[] = []
    if (t.status !== 'APPROVED') problemas.push('não está aprovado')
    if (t.formato !== 'NAMED') problemas.push('usa variáveis numeradas')
    if (t.cabecalho) problemas.push('tem imagem no cabeçalho')
    const faltam = uso.variaveis.filter((v) => !t.variaveis.includes(v))
    const sobram = t.variaveis.filter((v) => !uso.variaveis.includes(v))
    if (faltam.length) problemas.push(`faltam ${faltam.map((v) => `{{${v}}}`).join(', ')}`)
    if (sobram.length) problemas.push(`sobram ${sobram.map((v) => `{{${v}}}`).join(', ')}`)
    if (t.botoes.length !== uso.botoes.length)
        problemas.push(`${t.botoes.length} botão(ões), o uso espera ${uso.botoes.length}`)
    return problemas
}

/** A leitura mais recente da Meta entre os templates da cópia local. */
export function ultimaLeitura(templates: Pick<Template, 'lidoEm'>[]): string | undefined {
    return templates.reduce<string | undefined>(
        (max, t) => (t.lidoEm && (!max || t.lidoEm > max) ? t.lidoEm : max),
        undefined
    )
}
